import { useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, UploadCloud, Loader2, FileText, Check, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { normalizePhoneCz, checkSvpFile } from "../lib/bfu";
import { EmailHint, ResendCode } from "../components/BfuBits";
import { toast } from "sonner";

const STEPS = [
  { id: "contact", label: "Kontakt" },
  { id: "sms", label: "Ověření" },
  { id: "upload", label: "Nahrání ŠVP" },
];

const StepBar = ({ current }) => {
  const idx = STEPS.findIndex((s) => s.id === current);
  return (
    <div className="mb-6 flex items-center justify-center" data-testid="funnel-step-bar">
      {STEPS.map((s, i) => (
        <div key={s.id} className="flex items-center">
          <div className="flex flex-col items-center gap-1">
            <div className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold transition-colors
              ${i < idx ? "bg-emerald-600 text-white" : i === idx ? "bg-emerald-600 text-white ring-4 ring-emerald-100" : "bg-slate-100 text-slate-400"}`}>
              {i < idx ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`text-[11px] font-semibold ${i === idx ? "text-slate-900" : "text-slate-400"}`}>{s.label}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`mx-2 mb-4 h-0.5 w-10 rounded sm:w-14 ${i < idx ? "bg-emerald-500" : "bg-slate-200"}`} />}
        </div>
      ))}
    </div>
  );
};

export default function PublicStart() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const inputRef = useRef();
  const [step, setStep] = useState("contact");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [email, setEmail] = useState(params.get("email") || "");
  const [phone, setPhone] = useState(params.get("phone") || "");
  const [website, setWebsite] = useState("");
  const [intakeId, setIntakeId] = useState(null);
  const [devCode, setDevCode] = useState(null);
  const [code, setCode] = useState("");

  const sendSms = async () => {
    const norm = normalizePhoneCz(phone);
    if (!norm) {
      toast.error("Zkontrolujte prosím telefon — stačí ho napsat jako 777 123 456.");
      return false;
    }
    setPhone(norm);
    const { data } = await api.post("/public/intake/start", { email, phone: norm, website });
    setIntakeId(data.intake_id);
    setDevCode(data.dev_code || null);
    return true;
  };

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (!(await sendSms())) return;
      setStep("sms");
      toast.success("Ověřovací kód byl odeslán na váš telefon");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(errMsg(err));
        navigate(`/login?email=${encodeURIComponent(email)}`);
        return;
      }
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/public/intake/verify", { intake_id: intakeId, code });
      setUser(data.user);
      setStep("upload");
      toast.success("Telefon ověřen — nyní nahrajte své ŠVP");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const pickFile = (f) => {
    if (!f) return;
    const check = checkSvpFile(f);
    if (!check.ok) {
      toast.error(check.message, { duration: 9000 });
      return;
    }
    setFile(f);
  };

  const uploadAndRun = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Nahrajte prosím soubor se svým ŠVP (PDF nebo DOCX).");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: doc } = await api.post("/documents", fd);
      const { data: audit } = await api.post("/audits", { document_id: doc.id });
      toast.success("Kontrola vašeho ŠVP byla spuštěna");
      navigate(`/audits/${audit.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-lg fade-in">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>AuditŠVP</h1>
            <p className="text-xs text-slate-500">Diagnostika ŠVP vůči požadavkům RVP PV</p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm sm:p-9" data-testid="public-start-card">
          <StepBar current={step} />

          {step === "contact" && (
            <>
              <h2 className="text-xl font-bold text-slate-900">Zkontrolujte své ŠVP během pár minut</h2>
              <p className="mt-1 text-sm text-slate-500">
                Stačí e-mail a telefon. Pošleme vám SMS kód — hned poté nahrajete ŠVP a kontrola se spustí.
              </p>
              <form onSubmit={start} className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold">Váš e-mail *</Label>
                  <Input required type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
                    className="h-12 text-base" placeholder="reditelka@skolka.cz" data-testid="public-email-input" />
                  <EmailHint email={email} onFix={setEmail} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold">Váš telefon *</Label>
                  <Input required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                    onBlur={() => { const n = normalizePhoneCz(phone); if (n) setPhone(n); }}
                    className="h-12 text-base" placeholder="777 123 456" data-testid="public-phone-input" />
                  <p className="text-xs text-slate-400">Stačí napsat číslo bez předvolby — doplníme ji za vás.</p>
                </div>
                <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off"
                  className="pointer-events-none absolute -left-[9999px] h-px w-px opacity-0" aria-hidden="true" data-testid="hp-field-public" />
                <Button type="submit" disabled={busy} className="h-14 w-full bg-emerald-600 text-base font-bold hover:bg-emerald-700" data-testid="public-start-button">
                  {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
                  {busy ? "Odesílám…" : <>Pokračovat <ArrowRight className="ml-2 h-5 w-5" /></>}
                </Button>
                <p className="text-center text-xs text-slate-400">
                  Bezplatný náhled výsledků. Máte už účet? <Link to="/login" className="text-emerald-700 underline" data-testid="public-login-link">Přihlaste se</Link>
                </p>
              </form>
            </>
          )}

          {step === "sms" && (
            <>
              <h2 className="text-xl font-bold text-slate-900">Zadejte kód z SMS</h2>
              <p className="mt-1 text-sm text-slate-500">Poslali jsme vám SMS s 6místným kódem na číslo <b>{phone}</b>. Opište ho sem.</p>
              {devCode && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="public-dev-code">
                  DEV režim — váš kód: <b className="font-mono text-sm">{devCode}</b>
                </div>
              )}
              <form onSubmit={verify} className="mt-5 space-y-3">
                <Input required maxLength={6} inputMode="numeric" autoFocus value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="h-16 text-center text-3xl tracking-[0.4em] font-mono" placeholder="······" data-testid="public-code-input" />
                <Button type="submit" disabled={busy || code.length < 6} className="h-14 w-full bg-emerald-600 text-base font-bold hover:bg-emerald-700" data-testid="public-verify-button">
                  {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null} {busy ? "Ověřuji…" : "Ověřit telefon"}
                </Button>
                <ResendCode testId="public-resend-code" onResend={async () => {
                  try { await sendSms(); toast.success("Poslali jsme vám nový kód"); } catch (err) { toast.error(errMsg(err)); }
                }} />
                <button type="button" onClick={() => setStep("contact")} data-testid="public-back-button"
                  className="flex w-full items-center justify-center gap-1 py-1 text-center text-sm text-slate-500 underline hover:text-slate-700">
                  <ArrowLeft className="h-3.5 w-3.5" /> Zpět — opravit e-mail nebo telefon
                </button>
              </form>
            </>
          )}

          {step === "upload" && (
            <>
              <h2 className="text-xl font-bold text-slate-900">Ověřeno — nahrajte své ŠVP</h2>
              <p className="mt-1 text-sm text-slate-500">Poslední krok. Nahrajte dokument a kontrola se ihned spustí.</p>
              <form onSubmit={uploadAndRun} className="mt-6 space-y-4">
                <div
                  data-testid="public-upload-dropzone"
                  onClick={() => inputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
                  className={`cursor-pointer rounded-xl border-2 border-dashed p-10 text-center transition-colors
                    ${file ? "border-emerald-500 bg-emerald-50" : "border-emerald-300 bg-emerald-50/40 hover:border-emerald-500 hover:bg-emerald-50"}`}>
                  <input ref={inputRef} type="file" accept=".pdf,.docx" className="hidden" data-testid="public-upload-input"
                    onChange={(e) => pickFile(e.target.files?.[0])} />
                  {file ? (
                    <>
                      <Check className="mx-auto h-8 w-8 text-emerald-600" />
                      <div className="mt-2 flex items-center justify-center gap-2 text-base font-semibold text-emerald-800">
                        <FileText className="h-5 w-5" /> {file.name}
                      </div>
                      <p className="mt-1 text-xs text-slate-500">Soubor je připraven — pokračujte tlačítkem níže</p>
                    </>
                  ) : (
                    <>
                      <UploadCloud className="mx-auto h-10 w-10 text-emerald-600" />
                      <p className="mt-3 text-base font-bold text-slate-900">Klikněte sem a vyberte soubor se svým ŠVP</p>
                      <p className="mt-1 text-sm text-slate-500">PDF nebo DOCX · max. 50 MB</p>
                    </>
                  )}
                </div>
                <Button type="submit" disabled={busy || !file} className="h-14 w-full bg-emerald-600 text-base font-bold hover:bg-emerald-700" data-testid="public-run-audit-button">
                  {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <UploadCloud className="mr-2 h-5 w-5" />}
                  {busy ? "Nahrávám a spouštím kontrolu…" : "Spustit kontrolu ŠVP"}
                </Button>
                <p className="text-center text-xs text-slate-400">Přihlašovací údaje k účtu jsme vám poslali e-mailem.</p>
              </form>
            </>
          )}
        </div>
        <p className="mt-4 text-center text-xs text-slate-400">
          Automatizovaná kontrola je diagnostický podpůrný nástroj a nenahrazuje stanovisko MŠMT, NPI ani ČŠI.
        </p>
      </div>
    </div>
  );
}
